import { useReducer, useContext, useRef, useEffect, useMemo, useState, useCallback } from 'react';
import Skeleton from '@mui/material/Skeleton';
import { useMutation, useQuery } from '@tanstack/react-query';
import type {
  SmartRoutingCustomColumn,
  SmartRoutingNearbyEntitiesFilters,
  SmartRoutingNearbyEntitiesInput,
  SmartRoutingSearchEntity,
  SmartRoutingV2,
  SmartRoutingStartingLocation,
  LocationEntity,
} from '@trinity/chat';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { useActiveBrandIdentifierNullSafe } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier';
import { useDownloader } from '@trinity/chat-components/hooks/useDownloader';
import { ActiveChatSessionContext } from '../Chat/context';
import { BasicSystemUserMessage } from '../Chat/Message.System.Basic';
import { usePlaceholderStyles } from '../Chat/Message.System.styles';
import { MessageWrapper } from '../Chat/Message.System.Wrapper';
import { SmartRoutingEntitySelection } from './SmartRoutingEntitySelection';
import { SmartRoutingLocation } from './SmartRoutingLocation';
import { SmartRoutingResult } from './SmartRoutingResult';

type ExtendedSmartRoutingState = SmartRoutingV2 & {
  error?: string;
};

export type SmartRoutingAction =
  | { type: 'select-location' }
  | { type: 'submit-location'; payload: { startingLocation: SmartRoutingStartingLocation } }
  | { type: 'select-entities' }
  | { type: 'submit-route'; payload: { selectedEntities: LocationEntity[] } }
  | {
      type: 'route-generated';
      payload: { routingResult: NonNullable<SmartRoutingV2['routingResult']> };
    }
  | { type: 'set-error'; payload: string }
  | { type: 'reset' };

const smartRoutingReducer = (
  state: ExtendedSmartRoutingState,
  action: SmartRoutingAction
): ExtendedSmartRoutingState => {
  switch (action.type) {
    case 'submit-location':
      return {
        ...state,
        status: 'submit-location',
        startingLocation: action.payload.startingLocation,
        selectedEntities: null,
        routingResult: null,
        error: undefined,
      };
    case 'select-location':
      return {
        ...state,
        status: 'select-location',
      };
    case 'select-entities':
      return {
        ...state,
        status: 'select-entities',
      };
    case 'submit-route':
      return {
        ...state,
        status: 'submit-route',
        selectedEntities: action.payload.selectedEntities,
      };
    case 'route-generated':
      return {
        ...state,
        status: 'route-generated',
        routingResult: action.payload.routingResult,
      };
    case 'set-error':
      return {
        ...state,
        error: action.payload,
      };
    default:
      return state;
  }
};

type Props = {
  state: SmartRoutingV2;
  queryId: number;
  queryIdentifier: string;
};

export const SmartRoutingContainer = (props: Props) => {
  const [state, dispatch] = useReducer(smartRoutingReducer, {
    ...props.state,
    status: props.state.status ?? 'select-location',
    startingLocation: props.state.startingLocation ?? null,
  });

  const chat = useContext(ActiveChatSessionContext);
  const brandIdentifier = useActiveBrandIdentifierNullSafe();
  const placeholderStyles = usePlaceholderStyles();

  const apiClient = useChatApiClient();
  const download = useDownloader();

  const entitySelectionRef = useRef<HTMLDivElement>(null);
  const routingResultRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (state.status === 'select-entities' && entitySelectionRef.current) {
      setTimeout(() => {
        entitySelectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [state.status]);

  useEffect(() => {
    if (state.status === 'route-generated' && routingResultRef.current) {
      setTimeout(() => {
        routingResultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [state.status]);

  const subjectEntityId = props.state.subjectEntityId;
  // subjectEntityType was introduced alongside subjectEntityId; older persisted states may
  // predate the field, so fall back to 'hcp' (the only type that existed before it).
  const subjectEntityType = props.state.subjectEntityType ?? 'hcp';
  const validEntities = state.entities;
  const [searchFilters, setSearchFilters] = useState<SmartRoutingNearbyEntitiesFilters>({
    validEntities,
    distanceRange: {
      minMiles: 0,
      maxMiles: 50,
    },
  });

  const handleValidEntitiesFilterChange = useCallback((entityTypes: SmartRoutingV2['entities']) => {
    setSearchFilters((current) => {
      return {
        ...current,
        validEntities: entityTypes,
      };
    });
  }, []);

  const handleDistanceRangeFilterChange = useCallback(
    (distanceRange: { minMiles: number; maxMiles: number }) => {
      setSearchFilters((current) => {
        return {
          ...current,
          distanceRange,
        };
      });
    },
    []
  );

  const handleSearchTextChange = useCallback((searchText: string) => {
    setSearchFilters((current) => {
      return {
        ...current,
        searchText: searchText.length > 0 ? searchText : undefined,
      };
    });
  }, []);

  const nearbyEntitiesInput = useMemo<SmartRoutingNearbyEntitiesInput | null>(() => {
    const latitude = state.startingLocation?.latitude;
    const longitude = state.startingLocation?.longitude;

    if (typeof latitude !== 'number' || typeof longitude !== 'number') {
      return null;
    }

    return {
      brandIdentifier,
      locationQuery: {
        latitude,
        longitude,
      },
      filters: searchFilters,
    };
  }, [brandIdentifier, searchFilters, state.startingLocation?.latitude, state.startingLocation?.longitude]);

  const entityFetchEnabled = !!subjectEntityId && (!state.status || state.status === 'select-location');

  // eslint-disable-next-line @tanstack/query/exhaustive-deps
  const { data: fetchedEntity = null, isLoading: isFixedEntityLoading } = useQuery({
    // apiClient methods are intentionally excluded from queryKey because they are
    // non-serializable and do not define cache identity for this request.
    queryKey: ['entity-by-id', subjectEntityId, subjectEntityType, brandIdentifier],
    queryFn: async (): Promise<SmartRoutingSearchEntity | null> => {
      if (!subjectEntityId) return null;

      try {
        const { result } = await apiClient.smartRouting.getEntityById({
          brandIdentifier,
          entityId: subjectEntityId,
          entityType: subjectEntityType,
        });
        return result;
      } catch (error) {
        // A 404 (entity not found/inaccessible) is expected here and falls back to the
        // "no pre-seeded entity" state below, same as any other lookup failure.
        console.error('Failed to fetch entity by ID:', error);
      }
      return null;
    },
    enabled: entityFetchEnabled,
  });

  type EntitySearchData = {
    results: SmartRoutingSearchEntity[];
    customColumns: SmartRoutingCustomColumn[];
  };

  const {
    data: entitySearchData = { results: [], customColumns: [] },
    isLoading: isSearchLoading,
    isFetching: isSearchFetching,
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
  } = useQuery({
    // apiClient methods are intentionally excluded from queryKey because they are
    // non-serializable and do not define cache identity for this request.
    queryKey: [
      'entity-search',
      nearbyEntitiesInput?.locationQuery.latitude,
      nearbyEntitiesInput?.locationQuery.longitude,
      nearbyEntitiesInput?.brandIdentifier,
      nearbyEntitiesInput?.filters.validEntities.join(','),
      nearbyEntitiesInput?.filters.distanceRange.minMiles,
      nearbyEntitiesInput?.filters.distanceRange.maxMiles,
      nearbyEntitiesInput?.filters.territoryId,
      nearbyEntitiesInput?.filters.searchText,
    ],
    queryFn: async (): Promise<EntitySearchData> => {
      if (!nearbyEntitiesInput || nearbyEntitiesInput.filters.validEntities.length === 0) {
        return { results: [], customColumns: [] };
      }
      const result = await apiClient.smartRouting.searchNearbyEntities(nearbyEntitiesInput);
      return { results: result.results, customColumns: result.customColumns };
    },
    // Filter changes produce a new query key, so without carrying the previous results
    // forward every filter tweak would report `isLoading` and blank out the whole selection
    // UI. Keeping them makes a refetch report only `isFetching`.
    placeholderData: (previousResults) => previousResults,
    enabled:
      nearbyEntitiesInput !== null &&
      (state.status === 'select-entities' ||
        state.status === 'submit-route' ||
        state.status === 'route-generated'),
  });

  const entityResults = entitySearchData.results;
  const customColumns = entitySearchData.customColumns;

  const { mutateAsync: submitLocationAsync, isPending: _submitLocationPending } = useMutation({
    mutationFn: async (startingLocation: SmartRoutingStartingLocation) => {
      await apiClient.queries.updateQuerySmartRouting({
        brandIdentifier,
        chatInstanceId: chat.id,
        queryIdentifier: props.queryIdentifier,
        smartRoutingState: {
          ...state,
          status: 'submit-location',
          startingLocation,
          selectedEntities: null,
          routingResult: null,
        },
      });
    },
    onSuccess: () => {
      dispatch({ type: 'select-entities' });
    },
    onError: (error) => {
      const errorMessage = error instanceof Error ? error.message : 'API call failed';
      dispatch({ type: 'set-error', payload: errorMessage });
    },
  });

  const handleLocationSubmitted = async (startingLocation: SmartRoutingStartingLocation | null) => {
    if (!startingLocation) {
      dispatch({ type: 'set-error', payload: 'Starting location is required.' });
      return;
    }

    await submitLocationAsync(startingLocation);
  };

  const { mutateAsync: submitRouteAsync, isPending: submitRoutePending } = useMutation({
    mutationFn: async (selectedEntities: LocationEntity[]) => {
      const newState = await apiClient.queries.updateQuerySmartRouting({
        brandIdentifier,
        chatInstanceId: chat.id,
        queryIdentifier: props.queryIdentifier,
        smartRoutingState: {
          ...state,
          status: 'submit-route',
          selectedEntities,
          routingResult: null,
        },
      });

      const routingResult = newState.smartRoutingState.routingResult;
      if (!routingResult) {
        throw new Error('Route generation did not return a routing result');
      }

      return routingResult;
    },
    onSuccess: (routingResult) => {
      dispatch({
        type: 'route-generated',
        payload: { routingResult },
      });
    },
    onError: (error) => {
      const errorMessage = error instanceof Error ? error.message : 'API call failed';
      dispatch({ type: 'set-error', payload: errorMessage });
    },
  });

  const handleRouteSubmitted = async (selectedEntities: LocationEntity[]) => {
    if (selectedEntities.length < 2) {
      dispatch({
        type: 'set-error',
        payload: 'Please select at least two Accounts or HCPs to generate a route.',
      });
      return;
    }

    await submitRouteAsync(selectedEntities);
  };

  const { mutateAsync: exportNearbyEntitiesAsync, isPending: exportNearbyEntitiesPending } = useMutation({
    mutationFn: async (input: SmartRoutingNearbyEntitiesInput) => {
      return apiClient.smartRouting.exportNearbyEntitiesCsv(input);
    },
    onSuccess: ({ blob, filename }) => {
      download({
        blob,
        filename: filename ?? undefined,
      });
    },
    onError: (error) => {
      const errorMessage = error instanceof Error ? error.message : 'CSV export failed';
      dispatch({ type: 'set-error', payload: errorMessage });
    },
  });

  const handleExportNearbyEntities = async () => {
    if (!nearbyEntitiesInput) {
      dispatch({ type: 'set-error', payload: 'Starting location is required before export.' });
      return;
    }

    await exportNearbyEntitiesAsync(nearbyEntitiesInput);
  };

  return (
    <div>
      {!props.state.hideLocationForm && (
        <MessageWrapper>
          <BasicSystemUserMessage>
            <SmartRoutingLocation
              fixedEntity={fetchedEntity}
              isFixedEntityLoading={isFixedEntityLoading}
              validEntities={validEntities}
              value={state}
              disabled={state.status !== 'select-location'}
              dispatch={dispatch}
              onLocationSubmitted={handleLocationSubmitted}
            />
          </BasicSystemUserMessage>
        </MessageWrapper>
      )}
      {state.status === 'select-entities' ? (
        <MessageWrapper>
          <div ref={entitySelectionRef}>
            <SmartRoutingEntitySelection
              results={entityResults}
              customColumns={customColumns}
              validEntities={searchFilters.validEntities}
              availableEntityTypes={validEntities}
              onValidEntitiesFilterChange={handleValidEntitiesFilterChange}
              distanceRange={searchFilters.distanceRange}
              onDistanceRangeFilterChange={handleDistanceRangeFilterChange}
              searchText={searchFilters.searchText ?? ''}
              onSearchTextChange={handleSearchTextChange}
              onRouteSubmitted={handleRouteSubmitted}
              onExportResults={handleExportNearbyEntities}
              startingLocation={state.startingLocation}
              isLoading={isSearchFetching}
              isInitialLoading={isSearchLoading}
              isDisabled={submitRoutePending}
              isExportingResults={exportNearbyEntitiesPending}
            />
          </div>
        </MessageWrapper>
      ) : null}
      {state.status === 'submit-route' ? (
        <MessageWrapper>
          <BasicSystemUserMessage>
            <div css={placeholderStyles.skeleton}>
              <Skeleton css={placeholderStyles.skeletonItem} variant="text" width="95%" />
              <Skeleton css={placeholderStyles.skeletonItem} variant="text" width="80%" />
              <Skeleton css={placeholderStyles.skeletonItem} variant="text" width="65%" />
            </div>
          </BasicSystemUserMessage>
        </MessageWrapper>
      ) : null}
      {state.status === 'route-generated' && state.routingResult ? (
        <MessageWrapper>
          <div ref={routingResultRef}>
            <BasicSystemUserMessage>
              <SmartRoutingResult state={state} dispatch={dispatch} validEntities={validEntities} />
            </BasicSystemUserMessage>
          </div>
        </MessageWrapper>
      ) : null}
    </div>
  );
};
